import { Field, Formik } from 'formik';
import {
	CerebroDatePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroFileDragAndDrop,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
} from '../../shared/components';
import { FormikUtils } from '../../shared/utils';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from 'react-query';
import { CompanyNda, ParticipantCompany } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useApi } from '../../shared/context';
import { useSnackbar } from 'notistack';
import { Box, Grid } from '@mui/material';
import { QueryKeys, useNavigateToNextPage } from '../../shared/hooks';
import { format } from 'date-fns';
import { getApiResponseData } from '../../shared/utils/axios';
import { validateDateWithinPlusMinus100Years } from '../../shared/utils/date';

interface UploadNdaDrawerProps {
	open: boolean;
	dealGuid: string | undefined;
	companyGuid: string | undefined;
	isContactParentCompany: boolean;
	companies: ParticipantCompany[];
	onClose: () => void;
}

export const UploadNdaDrawer = ({
	open,
	dealGuid,
	companyGuid,
	isContactParentCompany,
	companies,
	onClose,
}: UploadNdaDrawerProps) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const navigateToNextPage = useNavigateToNextPage();

	let effectiveDate = format(new Date(), 'MM/dd/yyyy');
	let expirationDate = format(new Date(new Date().setFullYear(new Date().getFullYear() + 2)), 'MM/dd/yyyy');

	const uploadNdaMutation = useMutation(
		async ({ values }: UploadValuesMutationVariables) => {
			const uploads = values.uploads;
			let formData = new FormData();

			formData.append('effectiveDate', new Date(values.effectiveDate).toUTCString());
			formData.append('expirationDate', new Date(values.expirationDate).toUTCString());
			formData.append('companyGuid', values.companyGuid || '');

			if (uploads && uploads.length > 0) {
				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});
			}

			const { data, error } = await getApiResponseData<CompanyNda>(
				api.post(CerebroApiEndpoints.Deals.UploadNda(dealGuid), formData, {
					headers: { 'Content-Type': 'multipart/form-data' },
				})
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('NDA Saved.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.Deal.Nda(dealGuid));
				onClose();
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={onClose}>
			<Formik
				enableReinitialize
				validationSchema={UploadSchema}
				onSubmit={FormikUtils.MutateOnSubmit(uploadNdaMutation.mutate)}
				initialValues={{
					companyGuid: companyGuid,
					effectiveDate: effectiveDate,
					expirationDate: expirationDate,
					uploads: [] as File[],
				}}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => (
					<form onSubmit={handleSubmit} encType="multipart/form-data">
						<CerebroHeading size="h2" component="h3" gutterBottom>
							Upload Mutually Exclusive NDA
						</CerebroHeading>

						<Field
							as={CerebroFileDragAndDrop}
							name="uploads"
							sx={{ marginY: 3, width: '100%' }}
							onlyPdf={true}
							multiple={false}
						/>

						{isContactParentCompany && companies.length > 1 && (
							<Box sx={{ marginBottom: 2, marginTop: 2 }}>
								<CerebroLabel htmlFor="companyGuid" sx={{ marginTop: 3 }}>
									Company
								</CerebroLabel>
								<Field id="companyGuid" as={CerebroDropdown} name="companyGuid" disabled={isSubmitting}>
									{companies.map(company => (
										<CerebroDropdownMenuItem key={company.guid} value={company.guid}>
											{company.companyName}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</Box>
						)}

						<Box sx={{ marginBottom: 2, marginTop: 2 }}>
							<CerebroLabel
								required
								htmlFor="effectiveDate"
								error={FormikUtils.IsInvalid('effectiveDate', errors, touched)}
							>
								Effective Date
							</CerebroLabel>
							<Field
								component={CerebroDatePicker}
								name="effectiveDate"
								disabled={isSubmitting}
								error={FormikUtils.IsInvalid('effectiveDate', errors, touched)}
							/>
							{FormikUtils.IsInvalid('effectiveDate', errors, touched) && (
								<CerebroText variant="error">{errors.effectiveDate}</CerebroText>
							)}
						</Box>
						<Box sx={{ marginBottom: 2 }}>
							<CerebroLabel
								required
								htmlFor="expirationDate"
								error={FormikUtils.IsInvalid('expirationDate', errors, touched)}
							>
								Expiration Date
							</CerebroLabel>
							<Field
								component={CerebroDatePicker}
								name="expirationDate"
								disabled={isSubmitting}
								error={FormikUtils.IsInvalid('expirationDate', errors, touched)}
							/>
							{FormikUtils.IsInvalid('expirationDate', errors, touched) && (
								<CerebroText variant="error">{errors.expirationDate}</CerebroText>
							)}
						</Box>

						<Grid container sx={{ marginY: 3 }}>
							<Grid item>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Upload &amp; Execute NDA
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</form>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type UploadValuesMutationVariables = FormikUtils.MutateOnSubmitVariables<UploadValues>;
interface UploadValues {
	uploads: File[];
	effectiveDate: string;
	expirationDate: string;
	companyGuid: string | undefined;
}

const UploadSchema = Yup.object().shape({
	effectiveDate: Yup.date()
		.required('Must enter a valid effective date.')
		.test(
			'effectiveDate-range',
			'Effective Date must be within 100 years of today.',
			(value) => {
				if (!value) return true;
				return !validateDateWithinPlusMinus100Years(value, 'Effective Date');
			}
		),

	expirationDate: Yup.date()
		.required('Must enter a valid expiration date.')
		.test(
			'expirationDate-range',
			'Expiration Date must be within 100 years of today.',
			(value) => {
				if (!value) return true;
				return !validateDateWithinPlusMinus100Years(value, 'Expiration Date');
			}
		),

	companyGuid: Yup.string().optional(),
});
